import { useState } from "react";
import { LoadingText } from "@naf/teamscheme";
import ErrorMessage from "../../../shared/elements/ErrorMessage";
import { useAdminClient } from "../../api/AdminClient";
import { ReportDetailsLink } from "../report/details/ReportDetailsLink";

type PreviewResult = {
  count: number;
  previewedAt: string;
  earliestUnsignedScheduledArchival: string | null;
  latestUnsignedScheduledArchival: string | null;
};

export type ArchivalScheduleBackfillOutcome = "Updated" | "Skipped" | "Failed";

export type ArchivalScheduleBackfillReportResult = {
  reportId: string;
  outcome: ArchivalScheduleBackfillOutcome;
  previousUnsignedScheduledArchival: string | null;
  newUnsignedScheduledArchival: string | null;
  reason: string | null;
};

export type ArchivalScheduleBackfillResultBuckets = {
  updated: ArchivalScheduleBackfillReportResult[];
  skipped: ArchivalScheduleBackfillReportResult[];
  failed: ArchivalScheduleBackfillReportResult[];
};

const PREVIEW_PATH = "admin/reports/archive/scheduleBackfill/preview";
const APPLY_PATH = "admin/reports/archive/scheduleBackfill/apply";
const BATCH_LIMIT = 200;
const MAX_UPDATED_LINKS_SHOWN = 200;

export function emptyResultBuckets(): ArchivalScheduleBackfillResultBuckets {
  return { updated: [], skipped: [], failed: [] };
}

export function appendResultBatch(
  buckets: ArchivalScheduleBackfillResultBuckets,
  batch: ArchivalScheduleBackfillReportResult[],
): ArchivalScheduleBackfillResultBuckets {
  const next = {
    updated: [...buckets.updated],
    skipped: [...buckets.skipped],
    failed: [...buckets.failed],
  };
  for (const result of batch) {
    if (result.outcome === "Updated") next.updated.push(result);
    else if (result.outcome === "Skipped") next.skipped.push(result);
    else if (result.outcome === "Failed") next.failed.push(result);
    else {
      console.warn(
        `Unrecognized archival backfill outcome "${result.outcome}" for report ${result.reportId}; treating as failed.`,
      );
      next.failed.push(result);
    }
  }
  return next;
}

function persistFailSafe(buckets: ArchivalScheduleBackfillResultBuckets): boolean {
  try {
    localStorage.setItem(`archivalScheduleBackfill-${Date.now()}`, JSON.stringify(buckets));
    return true;
  } catch {
    // Best-effort fail-safe only — a full or unavailable localStorage should not break the tool.
    return false;
  }
}

export default function ArchivalScheduleBackfillTool() {
  const previewClient = useAdminClient<PreviewResult>();
  const applyClient = useAdminClient<ArchivalScheduleBackfillReportResult[]>();
  const [preview, setPreview] = useState<PreviewResult | null>(null);
  const [results, setResults] = useState<ArchivalScheduleBackfillResultBuckets | null>(null);
  const [running, setRunning] = useState(false);
  const [showUpdated, setShowUpdated] = useState(false);
  const [stoppedEarly, setStoppedEarly] = useState(false);
  const [persistFailed, setPersistFailed] = useState(false);

  async function onPreview() {
    const result = await previewClient.fetch({
      method: "POST",
      path: PREVIEW_PATH,
    });
    if (result) setPreview(result);
  }

  async function onRun() {
    if (!preview || preview.count === 0) return;

    setRunning(true);
    setStoppedEarly(false);
    setPersistFailed(false);
    setShowUpdated(false);
    let accumulated = emptyResultBuckets();
    setResults(accumulated);

    const body = {
      now: preview.previewedAt,
      earliestUnsignedScheduledArchival: preview.earliestUnsignedScheduledArchival,
      latestUnsignedScheduledArchival: preview.latestUnsignedScheduledArchival,
      limit: BATCH_LIMIT,
    };

    let stoppedEarlyThisRun = false;
    try {
      let updatedThisBatch = -1;
      while (updatedThisBatch !== 0) {
        const batch = await applyClient.fetch({
          method: "POST",
          path: APPLY_PATH,
          body,
        });
        if (batch === undefined) {
          stoppedEarlyThisRun = true;
          break;
        }
        updatedThisBatch = batch.filter((r) => r.outcome === "Updated").length;
        accumulated = appendResultBatch(accumulated, batch);
        setResults(accumulated);
      }
    } finally {
      if (stoppedEarlyThisRun) setStoppedEarly(true);
      setPersistFailed(!persistFailSafe(accumulated));
      setRunning(false);
    }

    await onPreview();
  }

  const totalProcessed = results
    ? results.updated.length + results.skipped.length + results.failed.length
    : 0;

  return (
    <div>
      <p>
        Re-schedules unsigned reports whose archival is already due or due
        within 8 days to a safe, staggered window (8-15 days out), so the
        archival-notification cron does not burst-send messages for the
        historical backlog. Already-archived reports are not touched.
      </p>
      <button type="button" disabled={previewClient.loading} onClick={onPreview}>
        {previewClient.loading ? <LoadingText text="Checking" /> : <span>Preview</span>}
      </button>
      {previewClient.error ? <ErrorMessage>{previewClient.error.message}</ErrorMessage> : null}
      {preview ? (
        <div>
          <p>{preview.count} report(s) need rescheduling.</p>
          <button type="button" disabled={running || preview.count === 0} onClick={onRun}>
            {running ? <LoadingText text={`Processed ${totalProcessed}`} /> : <span>Run backfill</span>}
          </button>
        </div>
      ) : null}
      {results ? (
        <div>
          <p>
            Updated {results.updated.length}, skipped {results.skipped.length}, failed{" "}
            {results.failed.length}.
          </p>
          {stoppedEarly && applyClient.error ? (
            <ErrorMessage>
              Run stopped early: {applyClient.error.message}. Results below reflect what was
              processed before the failure.
            </ErrorMessage>
          ) : null}
          {persistFailed ? (
            <ErrorMessage>
              Results were not saved as a local fail-safe copy this session (browser storage full or
              unavailable) — the results above are still accurate, just not recoverable after a refresh.
            </ErrorMessage>
          ) : null}
          {results.skipped.length > 0 ? (
            <div>
              <h4>Skipped</h4>
              <ul>
                {results.skipped.map((r) => (
                  <li key={r.reportId}>
                    <ReportDetailsLink reportId={r.reportId} /> {r.reason}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
          {results.failed.length > 0 ? (
            <div>
              <h4>Failed</h4>
              <ul>
                {results.failed.map((r) => (
                  <li key={r.reportId}>
                    <ReportDetailsLink reportId={r.reportId} /> {r.reason}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
          {results.updated.length > 0 ? (
            <div>
              <button type="button" onClick={() => setShowUpdated((v) => !v)}>
                {showUpdated ? "Hide" : "Show"} {results.updated.length} updated report(s)
              </button>
              {showUpdated ? (
                <div>
                  <ul>
                    {results.updated.slice(0, MAX_UPDATED_LINKS_SHOWN).map((r) => (
                      <li key={r.reportId}>
                        <ReportDetailsLink reportId={r.reportId} />
                      </li>
                    ))}
                  </ul>
                  {results.updated.length > MAX_UPDATED_LINKS_SHOWN ? (
                    <p>
                      Showing {MAX_UPDATED_LINKS_SHOWN} of {results.updated.length}.
                    </p>
                  ) : null}
                  <button
                    type="button"
                    onClick={() =>
                      navigator.clipboard.writeText(
                        results.updated.map((r) => r.reportId).join("\n"),
                      )
                    }
                  >
                    Copy all updated report IDs
                  </button>
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
